import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import ConsumercreateForm from "./ConsumercreateForm";
import ConsumerCSV from "./ConsumerCSV";
import { API_URLS } from "../../../../apiUrl";
import { ToastContainer, toast } from "react-toastify";
import axiosInstance from "../../../../components/axiosInstance";

type ViewType = "list" | "form" | "csv";

interface ConsumerItem {
  id: number;
  connection_number: string;
  holding_number: string | null;
  connection_start_date: string;
  status: string;
  opening_due_amount: string;
  is_opening_due_cleared: boolean;
  consumer_category_id: number;
  ferule_size: string;
  consumer_name: string | null;
  rate: string | null;
  street_id: number;
  mouza_name: string | null;
  created_at: string;
  updated_at: string;
  consumer_category_name: string;
  consumer_category_initial: string;
  phone_number: any;
  ward_name: any;
  advance_amount : number | null;
  street_no: any;
}

interface Props {
  onRedirectBorrow: () => void;
  onRedirectWard: () => void;
  onRedirect: () => void;
  StreetId: number;
  municipalityId: any
}

const ITEMS_PER_PAGE = 100;

const Consumerlist: React.FC<Props> = ({
  onRedirectBorrow,
  onRedirectWard,
  onRedirect,
  StreetId,
  municipalityId
}) => {
  const [consumerList, setConsumerList] = useState<ConsumerItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [view, setView] = useState<ViewType>("list");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeSearchRef = useRef<string>("");

  // ── Fetch full list (no search) ────────────────────────────────────────────
  const fetchConsumers = async (currentPage = 1) => {
    try {
      const response = await axiosInstance.post(
        `${API_URLS.consumerListing}/get`,
        { page: currentPage, limit: ITEMS_PER_PAGE, street_id: StreetId, municipality_id : municipalityId }
      );
      const resData = response.data?.data;
      setConsumerList(resData?.rows || []);
      const total = resData?.pagination?.totalValues || 0;
      setTotalPages(Math.ceil(total / ITEMS_PER_PAGE));
    } catch (error) {
      console.error("Error fetching Connection:", error);
    }
  };

  // ── Fetch with search query via API ────────────────────────────────────────
  const fetchBySearch = async (query: string, currentPage = 1) => {
    setIsSearching(true);
    try {
      const res = await axiosInstance.post(API_URLS.consumerDetail, {
        search: query,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        street_id: StreetId,      
        municipality_id: municipalityId  
      });
      const resData = res.data?.data;
      setConsumerList(resData?.rows || []);
      const total = resData?.pagination?.totalValues || 0;
      setTotalPages(Math.ceil(total / ITEMS_PER_PAGE));
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  // ── Debounced search handler ───────────────────────────────────────────────
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    activeSearchRef.current = value.trim();
    setPage(1);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (value.trim()) {
        fetchBySearch(value.trim(), 1);
      } else {
        fetchConsumers(1);
      }
    }, 500);
  };

  useEffect(() => {
    if (!StreetId) return;
    if (activeSearchRef.current) {
      fetchBySearch(activeSearchRef.current, page);
    } else {
      fetchConsumers(page);
    }
  }, [StreetId, page]);

  const filteredData = useMemo(() => consumerList, [consumerList]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setSearchQuery("");
  };

  const editconnection = (id: number) => {
    setEditingId(id);
    setIsEditing(true);
    setView("form");
  };

  const handleEditDone = () => {
    setEditingId(null);
    setIsEditing(false);
  };

  const handleSetView = (v: React.SetStateAction<ViewType>) => {
    setView(v);
    if (v === "list") {
      setEditingId(null);
      setIsEditing(false);
    }
  };

  const handleToggleStatus = async (item: ConsumerItem) => {
    if (togglingId === item.id) return;
    setTogglingId(item.id);

    const newStatus = item.status === "active" ? "inactive" : "active";
    try {
      await axiosInstance.post(API_URLS.consumerToggleStatus, {
        connection_id: String(item.id),
        municipality_id : municipalityId
      });

      setConsumerList((prev) =>
        prev.map((c) => (c.id === item.id ? { ...c, status: newStatus } : c))
      );

      toast.success(
        `Connection ${newStatus === "active" ? "activated" : "deactivated"} successfully`,
        { autoClose: 2000 }
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update status. Please try again.";

      toast.error(errorMessage, { autoClose: 3000 });
    } finally {
      setTogglingId(null);
    }
  };

  const isRole = localStorage.getItem("role_type");

  const downloadDemoCSV = async () => {
    try {
      const response = await axiosInstance.get(API_URLS.csvdownlaod, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "consumer_demo.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("CSV download error:", error);
    }
  };

  const TOTAL_COLS = 12;
  const wardName = consumerList[0]?.ward_name || "";
  const streetName = consumerList[0]?.street_no || "";


  return (
    <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      {/* Breadcrumb */}
      <nav className="bg-gradient-to-r from-gray-200 to-gray-100 py-2 px-4 mb-3 rounded-lg">
        <ul className="flex items-center space-x-1 text-xs text-gray-700 flex-wrap gap-y-1">
          {isRole !== "Municipality Admin" && (
            <>
              <li>
                <Link
                  to="/dashboard/overview"
                  className="hover:text-blue-600 hover:underline transition"
                >
                  Home
                </Link>
              </li>
              <li><span className="text-gray-400">/</span></li>
              <li>
                <a
                  href="/dashboard/view-municipality"
                  className="hover:text-blue-600 hover:underline transition"
                >
                  Municipalities
                </a>
              </li>
              <li><span className="text-gray-400">/</span></li>
            </>
          )}
          <li>
            <button onClick={onRedirectBorrow} className="hover:text-blue-600">
              Borough
            </button>
          </li>
          <li><span className="text-gray-400">/</span></li>
          <li>
            <button onClick={onRedirectWard} className="hover:text-blue-600">
              Ward
            </button>
          </li>
          <li><span className="text-gray-400">/</span></li>
          <li>
            <button onClick={onRedirect} className="hover:text-blue-600">
              Street
            </button>
          </li>
          <li><span className="text-gray-400">/</span></li>
          <li className="font-semibold text-gray-800">
            {view === "form"
              ? isEditing
                ? "Connection Edit"
                : "Connection Create"
              : "Connection List"}
          </li>
        </ul>
      </nav>

      <div style={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Form view — create OR edit */}
        {view === "form" && (
          <ConsumercreateForm
            setView={handleSetView}
            streetId={StreetId}
            refreshList={() => fetchConsumers(1)}
            consumerId={editingId}
            isEditing={isEditing}
            onEditDone={handleEditDone}
            municipalityId={municipalityId}
          />
        )}

        {view === "list" && (
          <div className="w-full min-w-0">
            {/* Header */}
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h1 className="text-xl font-semibold text-gray-800">
                Connection List for Ward - {wardName} & Street - {streetName}
              </h1>
              <div className="flex items-center gap-2 ml-auto flex-wrap">
                {/* Search */}
                <div className="relative flex items-center">
                  <input
                    type="text"
                    placeholder="Search name, connection no..."
                    className="border px-3 py-1.5 rounded-lg text-xs w-56 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  {isSearching && (
                    <span className="absolute right-3 text-xs text-gray-400 animate-pulse">
                      ...
                    </span>
                  )}
                </div>

                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-xs rounded-md shadow transition whitespace-nowrap"
                  onClick={() => {
                    setEditingId(null);
                    setIsEditing(false);
                    setView("form");
                  }}
                >
                  + Add Connection
                </button>
                <button
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs rounded-md shadow transition whitespace-nowrap"
                  onClick={() => setShowCsvModal(true)}
                >
                  Upload CSV
                </button>
                <button
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs rounded-md shadow transition whitespace-nowrap"
                  onClick={downloadDemoCSV}
                >
                  Demo CSV
                </button>
              </div>
            </div>

            {/* Table */}
            <div
              style={{
                width: "100%",
                overflowX: "auto",
                overflowY: "visible",
              }}
              className="bg-white rounded-xl shadow border border-gray-100"
            >
              <table className="table-auto text-xs" style={{ width: "max-content", minWidth: "100%" }}>
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Connection Name</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Connection ID</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Start Date</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Phone</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Mouza</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Status</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Opening Due</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Ferrule</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Category</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Rate (₹)</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Advance Amount (₹)</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Action</th>
                    <th className="py-2 px-3 text-left font-semibold text-gray-600 whitespace-nowrap">Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={TOTAL_COLS}
                        className="text-center py-8 text-gray-400"
                      >
                        No Connection found
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b border-gray-100 hover:bg-blue-50 transition"
                      >
                        {/* Connection Name */}
                        <td className="py-2 px-3 text-gray-800 max-w-[130px] truncate" title={item.consumer_name || ""}>
                          {item.consumer_name || (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>

                        {/* Connection ID */}
                        <td className="py-2 px-3 font-mono text-gray-700 whitespace-nowrap">
                          {item.connection_number}
                        </td>

                        {/* Start Date */}
                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">
                          {new Date(item.connection_start_date).toLocaleDateString("en-GB")}
                        </td>

                        {/* Phone */}
                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">
                          {item.phone_number || <span className="text-gray-400">N/A</span>}
                        </td>

                        {/* Mouza */}
                        <td className="py-2 px-3 text-gray-600 max-w-[110px] truncate" title={item.mouza_name || ""}>
                          {item.mouza_name || <span className="text-gray-400">—</span>}
                        </td>

                        {/* Status */}
                        <td className="py-2 px-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              item.status === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-600"
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>

                        {/* Opening Due */}
                        <td className="py-2 px-3 whitespace-nowrap">
                          {Number(item.opening_due_amount) > 0 ? (
                            <span
                              className={`font-semibold ${
                                item.is_opening_due_cleared
                                  ? "text-gray-400 line-through"
                                  : "text-red-600"
                              }`}
                            >
                              ₹{Number(item.opening_due_amount).toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        {/* Ferule Size */}
                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">
                          {item.ferule_size}"
                        </td>

                        {/* Category */}
                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">
                          {item.consumer_category_name}
                          {item.consumer_category_initial && (
                            <span className="text-gray-400 ml-0.5">({item.consumer_category_initial})</span>
                          )}
                        </td>

                        {/* Rate */}
                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">
                          {item.rate || <span className="text-gray-400">—</span>}
                        </td>

                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">
                          {item?.advance_amount ?? <span className="text-gray-400">—</span>}
                        </td>

                        {/* Toggle Status */}
                        <td className="py-2 px-3">
                          <button
                            onClick={() => handleToggleStatus(item)}
                            disabled={togglingId === item.id}
                            title={
                              item.status === "active"
                                ? "Click to Deactivate"
                                : "Click to Activate"
                            }
                            style={{
                              width: "80px",
                              height: "24px",
                              borderRadius: "999px",
                              border: "none",
                              cursor: togglingId === item.id ? "not-allowed" : "pointer",
                              opacity: togglingId === item.id ? 0.6 : 1,
                              position: "relative",
                              backgroundColor: item.status === "active" ? "#22c55e" : "#d1d5db",
                              transition: "background-color 0.3s ease",
                              color: "#fff",
                              fontSize: "10px",
                              fontWeight: 600,
                            }}
                          >
                            <span
                              style={{
                                position: "absolute",
                                left: item.status === "active" ? "8px" : "30px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                transition: "all 0.3s ease",
                                pointerEvents: "none",
                              }}
                            >
                              {togglingId === item.id
                                ? "..."
                                : item.status === "active"
                                ? "Active"
                                : "Inactive"}
                            </span>
                            <span
                              style={{
                                position: "absolute",
                                top: "3px",
                                left: item.status === "active" ? "57px" : "3px",
                                width: "18px",
                                height: "18px",
                                borderRadius: "50%",
                                backgroundColor: "#ffffff",
                                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                                transition: "all 0.3s ease",
                              }}
                            />
                          </button>
                        </td>

                        {/* Edit */}
                        <td className="py-2 px-3">
                          <button
                            onClick={() => editconnection(item.id)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition text-xs font-medium"
                            title="Edit Connection"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3.5 w-3.5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center mt-4">
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size="small"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSV Modal */}
      {showCsvModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowCsvModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <ConsumerCSV
              streetId={StreetId}
              setView={() => {
                setShowCsvModal(false);
                fetchConsumers(1);
              }}
              municipalityId = {municipalityId}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Consumerlist;